// INVPOLISH1 (docs/build-briefs/INVPOLISH1-inventory-leftover-fixes.md). The words on a Past counts row: who counted, how many
// areas and items with the units per item type, and how far off the count was. DOM-free so a node test reads it directly.
// Money is never worked out here: the server sends the stock-value valuation's dollars and this only words them.

import { resolveActorDisplayName } from "./actorDisplay.ts";
import { signedMoney, signedPercent } from "./countTableModel.ts";
import type { CountHistorySummaryDto } from "./api.ts";

const TYPE_WORDS: Readonly<Record<string, readonly [string, string]>> = {
  bottle: ["bottle", "bottles"],
  keg: ["keg", "kegs"],
  can: ["can", "cans"],
};

function typeWords(containerType: string | null, count: number): string {
  const words = containerType === null ? undefined : TYPE_WORDS[containerType];
  if (words === undefined) return count === 1 ? "other item" : "other items";
  return count === 1 ? words[0] : words[1];
}

/** "12 bottles, 2 kegs". */
export function formatItemTypeCounts(types: readonly { containerType: string | null; count: number }[]): string {
  return types.map((type) => `${type.count} ${typeWords(type.containerType, type.count)}`).join(", ");
}

export interface CountHistoryLine {
  who: string;
  scope: string;
  /** Null when the count carries no money (no right to read it, or no item had a cost). */
  off: string | null;
}

export function countHistoryLine(params: {
  counterName: string | null;
  summary: Pick<CountHistorySummaryDto, "areaCount" | "itemCount" | "itemTypes" | "value">;
}): CountHistoryLine {
  const { summary } = params;
  const parts: string[] = [];
  if (summary.areaCount > 0) parts.push(`${summary.areaCount} ${summary.areaCount === 1 ? "area" : "areas"}`);
  parts.push(`${summary.itemCount} ${summary.itemCount === 1 ? "item" : "items"}`);
  const types = formatItemTypeCounts(summary.itemTypes);
  const scope = types === "" ? parts.join(", ") : `${parts.join(", ")}: ${types}`;
  let off: string | null = null;
  if (summary.value !== null) {
    off =
      summary.value.differenceCents === 0
        ? "On the money"
        : `Off by ${signedMoney(summary.value.differenceCents)}${summary.value.percent === null ? "" : ` (${signedPercent(summary.value.percent)})`}`;
  }
  return { who: `Counted by ${resolveActorDisplayName(params.counterName)}`, scope, off };
}

/** A keg's count was stored in the bottle unit while its figure is kegs (the walk found "3.7 bottles, was 4 bottles" on a
 * keg). The item's own container type names the unit; every other pairing reads as stored. */
export function historyUnitFor(storedUnit: string, containerType: string | null): string {
  return storedUnit === "bottle" && containerType === "keg" ? "keg" : storedUnit;
}
